const STATE_STORAGE_KEY = "safeplate-state";
const LEGACY_BASE_STATE_KEY = "safePlateState_v1";
const LEGACY_AUTH_USER_KEY = "safePlateAuthUser_v1";
export const STATE_CHANGED_EVENT = "safeplate-state-changed";
let remoteSaveTimer = null;

function defaultState() {
  const now = new Date().toISOString();
  return {
    parentPasscode: "1234",
    children: [],
    activeChildId: null,
    createdAt: now,
    updatedAt: now,
  };
}

function looksLikeState(value) {
  return (
    value &&
    typeof value === "object" &&
    (Array.isArray(value.children) ||
      Object.prototype.hasOwnProperty.call(value, "activeChildId") ||
      Object.prototype.hasOwnProperty.call(value, "parentPasscode"))
  );
}

function normalizeState(value) {
  const base = defaultState();
  if (!looksLikeState(value)) return base;

  const children = Array.isArray(value.children) ? value.children : [];
  return {
    ...base,
    ...value,
    children,
    activeChildId: value.activeChildId || children[0]?.id || null,
    createdAt: value.createdAt || base.createdAt,
    updatedAt: value.updatedAt || base.updatedAt,
  };
}

function parseStoredState(key) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!looksLikeState(parsed)) return null;
    return { key, state: normalizeState(parsed) };
  } catch {
    return null;
  }
}

function legacyStateKeys() {
  const keys = new Set([LEGACY_BASE_STATE_KEY]);

  try {
    const rawUser = localStorage.getItem(LEGACY_AUTH_USER_KEY);
    const authUser = rawUser ? JSON.parse(rawUser) : null;
    if (authUser?.id) keys.add(`${LEGACY_BASE_STATE_KEY}_${authUser.id}`);
  } catch {
    // Ignore broken auth records; the unscoped legacy key may still exist.
  }

  try {
    for (let index = 0; index < localStorage.length; index += 1) {
      const key = localStorage.key(index);
      if (key?.startsWith(`${LEGACY_BASE_STATE_KEY}_`)) keys.add(key);
    }
  } catch {
    // Some browsers can block storage enumeration. The known keys above are enough.
  }

  keys.delete(STATE_STORAGE_KEY);
  return Array.from(keys);
}

function stateTime(candidate) {
  return Date.parse(candidate.state.updatedAt || candidate.state.createdAt || "") || 0;
}

function compareLegacyStates(a, b) {
  const childDifference = (b.state.children?.length || 0) - (a.state.children?.length || 0);
  if (childDifference) return childDifference;
  return stateTime(b) - stateTime(a);
}

function findLegacyState() {
  const candidates = legacyStateKeys().map(parseStoredState).filter(Boolean);
  if (!candidates.length) return null;
  return candidates.sort(compareLegacyStates)[0];
}

function notifyStateChanged() {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event(STATE_CHANGED_EVENT));
  }
}

function writeLocalState(state) {
  localStorage.setItem(STATE_STORAGE_KEY, JSON.stringify(state));
  notifyStateChanged();
  return state;
}

async function pushRemoteState(state) {
  if (typeof fetch !== "function") return null;

  const response = await fetch("/api/state", {
    method: "PUT",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ state }),
  });

  if (response.status === 401 || response.status === 403 || response.status === 503) return null;
  if (!(response.headers.get("content-type") || "").includes("application/json")) {
    throw new Error("Account server is not available on this preview.");
  }
  if (!response.ok) throw new Error("SafePlate could not save this profile to the account.");
  return response.json();
}

function scheduleRemoteSave(state) {
  if (typeof window === "undefined") return;
  window.clearTimeout(remoteSaveTimer);
  remoteSaveTimer = window.setTimeout(() => {
    pushRemoteState(state).catch(() => {
      // Keep the local save. The next profile edit or login can try again.
    });
  }, 300);
}

export function loadState() {
  try {
    const raw = localStorage.getItem(STATE_STORAGE_KEY);
    if (raw) return normalizeState(JSON.parse(raw));

    const legacy = findLegacyState();
    if (legacy) {
      const migrated = normalizeState({
        ...legacy.state,
        migratedFrom: legacy.key,
        migratedAt: new Date().toISOString(),
      });
      localStorage.setItem(STATE_STORAGE_KEY, JSON.stringify(migrated));
      return migrated;
    }

    return defaultState();
  } catch {
    return defaultState();
  }
}

export function saveState(state) {
  const next = normalizeState({ ...(state || {}), updatedAt: new Date().toISOString() });
  writeLocalState(next);
  scheduleRemoteSave(next);
  return next;
}

export function replaceLocalState(state) {
  const next = normalizeState({ ...(state || {}), updatedAt: state?.updatedAt || new Date().toISOString() });
  return writeLocalState(next);
}

export async function loadAccountState() {
  if (typeof fetch !== "function") return null;

  const response = await fetch("/api/state", {
    method: "GET",
    credentials: "include",
  });

  if (response.status === 401 || response.status === 403 || response.status === 503) return null;
  if (!(response.headers.get("content-type") || "").includes("application/json")) {
    throw new Error("Account server is not available on this preview.");
  }
  if (!response.ok) throw new Error("SafePlate could not load the account profile.");

  const data = await response.json();
  if (data?.state) return replaceLocalState(data.state);
  return null;
}

export async function saveCurrentStateToAccount() {
  const data = await pushRemoteState(loadState());
  if (data?.state) return replaceLocalState(data.state);
  return null;
}

export function getParentPasscode() {
  const s = loadState();
  return s.parentPasscode || "1234";
}

export function setParentPasscode(passcode4) {
  const s = loadState();
  s.parentPasscode = String(passcode4 || "").slice(0, 4);
  return saveState(s);
}
